const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss' : 'ws'
export const IS_MOBILE = window.matchMedia?.('(max-width: 640px)')?.matches ?? false

function isPagesPreviewHost(hostname) {
  const host = String(hostname || '').toLowerCase()
  return Boolean(host) && host.endsWith('.pages.dev')
}

function normalizeWsBaseUrl(input, { defaultPath } = { defaultPath: '/xiaozhi/v1/' }) {
  const raw = String(input || '').trim()
  if (!raw) return ''

  const ensurePath = (url) => {
    try {
      const u = new URL(url)
      if (!u.pathname || u.pathname === '/' || u.pathname === '') u.pathname = defaultPath
      if (!u.pathname.endsWith('/')) u.pathname += '/'
      u.search = ''
      u.hash = ''
      return u.toString()
    } catch {
      return ''
    }
  }

  // Accept http(s) and convert to ws(s)
  if (/^https?:\/\//i.test(raw)) {
    const converted = raw.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:')
    return ensurePath(converted)
  }

  // Accept ws(s)
  if (/^wss?:\/\//i.test(raw)) {
    return ensurePath(raw)
  }

  // Accept host[:port][/path]
  if (raw.startsWith('//')) {
    return ensurePath(`${WS_PROTOCOL}:${raw}`)
  }
  return ensurePath(`${WS_PROTOCOL}://${raw}`)
}

function normalizeWsPath(input) {
  const raw = String(input || '').trim()
  if (!raw) return ''
  let p = raw
  if (!p.startsWith('/')) p = `/${p}`
  if (!p.endsWith('/')) p = `${p}/`
  return p
}

export function buildWsUrlCandidates(targetDeviceId) {
  const deviceId = String(targetDeviceId || '').trim() || 'public-test'
  const params = new URLSearchParams()
  params.set('device-id', deviceId)
  params.set('client-id', 'web-listener')

  const envWs = String(import.meta.env.VITE_WS_URL || '').trim()
  const apiOrigin = String(import.meta.env.VITE_API_ORIGIN || '').trim()
  const qs = `?${params.toString()}`

  const customPath = normalizeWsPath(import.meta.env.VITE_WS_PATH)
  const wsPaths = [customPath, '/chamii/v1/', '/xiaozhi/v1/'].filter(Boolean)

  const hostedDevBases = (() => {
    try {
      const host = window.location.hostname
      if (!isPagesPreviewHost(host)) return []
      // Pages preview 域名本身没有绑定 WS Worker 路由；强制回落到 admin-dev 上的 Worker。
      return wsPaths.map((path) => `wss://admin-dev.chamii.com${path}`)
    } catch {
      return []
    }
  })()

  const sameOriginBases = wsPaths.map((path) => `${WS_PROTOCOL}://${window.location.host}${path}`)
  const legacyPortBases =
    window.location.protocol === 'http:'
      ? wsPaths.map((path) => `ws://${window.location.hostname}:30001${path}`)
      : []

  const envWsBases = envWs ? wsPaths.map((path) => normalizeWsBaseUrl(envWs, { defaultPath: path })) : []

  const apiWsBase = (() => {
    // If API origin is absolute, try deriving a WS endpoint from it.
    // This supports a Worker-based "ws proxy" deployment where WS lives on the API host.
    if (!apiOrigin) return ''
    if (!/^https?:\/\//i.test(apiOrigin)) return ''
    return '' // handled below (multi-path)
  })()

  const apiWsBases = /^https?:\/\//i.test(apiOrigin)
    ? wsPaths.map((path) => normalizeWsBaseUrl(apiOrigin, { defaultPath: path }))
    : []

  const candidates = [...envWsBases, ...apiWsBases, ...hostedDevBases, ...sameOriginBases, ...legacyPortBases]
    .filter(Boolean)
    .map((base) => `${base}${qs}`)

  // Deduplicate while preserving order.
  const seen = new Set()
  return candidates.filter((u) => (seen.has(u) ? false : (seen.add(u), true)))
}

export async function fetchLastDeviceId() {
  try {
    const res = await fetch('/api/device/last', { cache: 'no-store' })
    if (!res.ok) return ''
    const data = await res.json()
    return data?.device_id || ''
  } catch {
    return ''
  }
}

